import { ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useAgentName } from '../../../lib/use-agent-name';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { type WebOpportunity } from '../../../lib/opportunities';
import { canExpandOpportunities, pileOrder } from '../../../lib/opportunity-pile';
import { useActiveConversation } from '../../chat/ActiveConversationContext';
import { Button, TextButton, Typography } from '../ui';
import { HeightMorph } from './HeightMorph';
import { OpportunitiesExpanded } from './OpportunitiesExpanded';
import styles from './OpportunityDeck.module.css';

// The MOBILE (stacked-band) opportunities deck (Figma 11922:69552): a header ("Opportunities" +
// subtitle) over a VERTICAL peek-stack of agent-proposed outcomes (OPPORTUNITY goals). The front card
// is open (workspace eyebrow, accented title, "Talk to …"); the ≤2 behind peek their heads above it,
// each a touch inset. Clicking a peeking card brings it to the front (native's deck reorder) —
// tap-to-raise only, NO swipe.
//
// "View More (N)" expands IN PLACE, inside this same slot: the deck is replaced by the full vertical
// list (`OpportunitiesExpanded`), its height animated open/closed by `HeightMorph`, while the header
// stays put. "View Less" collapses it back. Dismissing lives on the expanded list's ✕, like native.
//
// The desktop (wide-band) rendering is the horizontal left-fan `OpportunityFanDeck`; `OpportunityPile`
// switches between the two on the band's stack breakpoint.

const PAGE = 'home';

interface Props {
  opportunities: WebOpportunity[];
  /** Resolves the card's eyebrow — the owning workspace's name (the pile aggregates every workspace). */
  workspaceName: (opportunity: WebOpportunity) => string | undefined;
}

export function OpportunityDeck({ opportunities, workspaceName }: Props) {
  const { t } = useTranslation();
  const [frontId, setFrontId] = useState<string | null>(null);
  const { logPageClickBi } = useGenericBi();
  const ordered = pileOrder(opportunities, frontId);
  const canExpand = canExpandOpportunities(opportunities.length);

  // The deck ↔ full-list swap is a measured height-morph (see `HeightMorph`); this is just the boolean.
  const [expanded, setExpanded] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const expand = () => {
    logPageClickBi(
      { page_name: PAGE, button_name: 'opportunities_view_more' },
      { componentName: 'opportunities-view-more' },
    );
    setExpanded(true);
  };
  const collapse = () => {
    // Collapsing from the bottom of a long list would strand the viewport below the now-short section
    // (an empty screen above the dashboard) — bring the section header back into view, like native's
    // `scrollProxy.scrollTo(anchor, .top)`. Snap it INSTANTLY (not smooth) FIRST, then shrink: a smooth
    // scroll running WHILE the height animates fights it — as the page shrinks the scroll position keeps
    // re-clamping, which is the jitter. So exactly one thing animates (the height). `'start'` (not
    // `'nearest'`) because the expanded section is taller than the viewport, which `'nearest'` no-ops on.
    sectionRef.current?.scrollIntoView({ block: 'start' });
    setExpanded(false);
  };

  // If everything gets abandoned or the set shrinks below the pile, drop back to the deck.
  useEffect(() => {
    if (!canExpand) setExpanded(false);
  }, [canExpand]);

  const cardEls = useRef(new Map<string, HTMLElement>());
  const registerCard = useCallback(
    (id: string) => (el: HTMLElement | null) => {
      if (el) cardEls.current.set(id, el);
      else cardEls.current.delete(id);
    },
    [],
  );

  // FLIP the reorder: the layout swap is instant (margin-bottom is not transitioned), so we slide EVERY
  // card that moved — the one coming forward AND the one receding — from where it just was to where it
  // landed. Animating both (not just the forward card) is what keeps the swap from half-snapping. The
  // vertical slide here and the horizontal inset (`margin-inline`, in the stylesheet) run the SAME
  // duration + easing, so a card moving diagonally travels a straight path instead of bending.
  const prevTops = useRef(new Map<string, number>());
  const orderKey = ordered.map((o) => o.id).join(',');
  useLayoutEffect(() => {
    const prev = prevTops.current;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const next = new Map<string, number>();
    cardEls.current.forEach((el, id) => {
      const top = el.getBoundingClientRect().top;
      next.set(id, top);
      const was = prev.get(id);
      if (was !== undefined && !reduce && was !== top) {
        el.animate(
          [{ transform: `translateY(${was - top}px)` }, { transform: 'translateY(0px)' }],
          { duration: 420, easing: 'cubic-bezier(0.2, 0.8, 0.25, 1)' },
        );
      }
    });
    prevTops.current = next;
  }, [orderKey]);

  if (ordered.length === 0) return null;

  const deck = (
    <div className={styles.stack}>
      {ordered.map((opportunity, i) => (
        <OpportunityCard
          key={opportunity.id}
          opportunity={opportunity}
          workspace={workspaceName(opportunity)}
          front={i === ordered.length - 1}
          depth={ordered.length - 1 - i}
          registerRef={registerCard(opportunity.id)}
          onBringForward={() => setFrontId(opportunity.id)}
        />
      ))}
    </div>
  );

  return (
    <div ref={sectionRef} className={styles.pile}>
      <header className={styles.header}>
        <Typography variant="h2" color="strong" component="h2" className={styles.heading}>
          {t('home.opportunities.panelTitle')}
        </Typography>
        <Typography variant="p2" color="secondary" component="p" className={styles.subheading}>
          {t('home.opportunities.heading')}. {t('home.opportunities.subheading')}
        </Typography>
      </header>
      {canExpand ? (
        <HeightMorph
          expanded={expanded}
          collapsed={
            <>
              {deck}
              <div className={styles.toggleRow}>
                <TextButton
                  size="small"
                  className={styles.viewToggle}
                  endIcon={<ChevronDown size={18} aria-hidden />}
                  onClick={expand}
                >
                  {t('home.opportunities.viewMore', { count: opportunities.length })}
                </TextButton>
              </div>
            </>
          }
          expandedContent={
            <OpportunitiesExpanded
              opportunities={opportunities}
              workspaceName={workspaceName}
              onViewLess={collapse}
            />
          }
        />
      ) : (
        deck
      )}
    </div>
  );
}

function OpportunityCard({
  opportunity,
  workspace,
  front,
  depth,
  registerRef,
  onBringForward,
}: {
  opportunity: WebOpportunity;
  workspace?: string;
  front: boolean;
  depth: number;
  registerRef: (el: HTMLElement | null) => void;
  onBringForward: () => void;
}) {
  const { t } = useTranslation();
  const { short } = useAgentName();
  const { logPageClickBi } = useGenericBi();
  const { openChatWithMessage } = useActiveConversation();

  // A peeking card shows only its head, so its eyebrow names the opportunity itself — that strip is all
  // the user has to tell the cards apart. The open card's eyebrow is its workspace instead.
  const eyebrow = front ? workspace : opportunity.title;

  const talk = () => {
    logPageClickBi({ page_name: PAGE, button_name: 'opportunity_talk' }, { componentName: 'opportunity-talk' });
    openChatWithMessage({
      kind: 'new',
      message: t('home.opportunities.chatPrompt', { title: opportunity.title }),
    });
  };

  return (
    <article ref={registerRef} className={styles.card} data-front={front || undefined} data-depth={depth}>
      {eyebrow && (
        <Typography variant="h4" color="strong" component="p" noWrap className={styles.eyebrow}>
          {eyebrow}
        </Typography>
      )}
      {front ? (
        <>
          <Typography variant="h2" color="strong" component="h3" className={styles.title}>
            <AccentedTitle title={opportunity.title} />
          </Typography>
          <div className={styles.actions}>
            <Button onClick={talk}>{t('home.opportunities.cta', { name: short })}</Button>
          </div>
        </>
      ) : (
        // The WHOLE peeking card brings it to the front (native's tap-to-raise), not just its head strip.
        <button
          type="button"
          className={styles.raise}
          aria-label={t('home.opportunities.bringForward', { title: opportunity.title })}
          onClick={() => {
            onBringForward();
            logPageClickBi({ page_name: PAGE, button_name: 'raise_opportunity_card' });
          }}
        />
      )}
    </article>
  );
}

// The design sets one interior word of the headline in the serif face for editorial contrast (Figma
// 11922:69552 — "Pre-Order Landing Page Build"). The opportunity carries no such marker, so the accent
// is chosen deterministically — the second word of a multi-word title — keeping the flourish stable per
// proposal without a backend contract change.
function AccentedTitle({ title }: { title: string }) {
  const words = title.split(' ');
  if (words.length < 3) return <>{title}</>;
  return (
    <>
      {words[0]} <span className={styles.accent}>{words[1]}</span> {words.slice(2).join(' ')}
    </>
  );
}
